import { useTheme } from "@emotion/react";
import { DeleteOutlined, EditOutlined } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteDivision,
  fetchDivisions,
  reassignAssessments,
} from "reduxStore/actions/divisionActions";
import { setSnackbar } from "reduxStore/state";
import { checkRoles, getPermissionFlags, getUserScope } from "utils/permissions";

export const useDivisionGrid = ({ handleEditOpen }) => {
  const { palette } = useTheme();
  const user = useSelector((state) => state.user);

  const { locationIds } = getUserScope(user);
  const userLocationId = locationIds[0] || "";

  const { showOrg } = getPermissionFlags(user);
  const { isAcademyImprovementLead } = checkRoles(user);

  const backgroundAlt = palette.background.alt;
  const primaryLight = palette.primary.light;
  const primaryDark = palette.primary.dark;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("");

  const [searchInput, setSearchInput] = useState("");

  const dispatch = useDispatch();

  const divisions = useSelector((state) => state.divisions.data || []);
  const totalDivisions = useSelector((state) => state.divisions.total || 0);

  const [deleteDivisionDialogOpen, setDeleteDivisionDialogOpen] =
    useState(false);
  const [divisionToDelete, setDivisionToDelete] = useState(null);

  const [
    reassignDivisionAssessmentsDialogOpen,
    setReassignDivisionAssessmentsDialogOpen,
  ] = useState(false);

  const [divisionToReassign, setDivisionToReassign] = useState(null);

  const [sortModel, setSortModel] = useState([
    { field: "createdAt", sort: "desc" }, // Default sorting by createdAt
  ]);

  const handleDeleteOpen = (division) => {
    setDivisionToDelete(division);
    setDeleteDivisionDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDivisionToDelete(null);
    setDeleteDivisionDialogOpen(false);
  };

  const handleDeleteDivisionConfirm = async () => {
    try {
      const response = await dispatch(deleteDivision(divisionToDelete._id));

      if (response.associatedAssessments) {
        setDivisionToReassign(divisionToDelete._id);
        setReassignDivisionAssessmentsDialogOpen(true);
        return;
      }

      const isSuccess = response.success && response.message;
      dispatch(
        setSnackbar({
          open: true,
          message: response.message,
          severity: isSuccess ? "success" : "error",
        })
      );

      if (isSuccess) {
        closeDeleteDialog();
        setDivisionToDelete(null);
        setDeleteDivisionDialogOpen(false);
      }
    } catch (error) {
      console.error("Error while deleting division:", error);
      dispatch(
        setSnackbar({
          open: true,
          severity: "error",
          message: "Error while deleting division",
        })
      );
    }
  };

  // handle reassignment confirmation
  const handleReassignConfirm = async (newDivisionId) => {
    const response = await dispatch(
      reassignAssessments(divisionToReassign, newDivisionId)
    );

    if (response.success) {
      dispatch(
        setSnackbar({
          open: true,
          severity: "success",
          message: response.message,
        })
      );
      setReassignDivisionAssessmentsDialogOpen(false);

      // Retry deleting the division
      await handleDeleteDivisionConfirm();
    } else {
      dispatch(
        setSnackbar({
          open: true,
          message: response.message,
          severity: "error",
        })
      );
    }
  };

  useEffect(() => {
    const filters = {
      page, // Ensure it aligns with API expectations
      pageSize, // API expects 'limit' instead of 'pageSize'
      sort: sortModel.length > 0 ? sortModel[0].field : "createdAt", // Default to createdAt if no sort model
      order: sortModel.length > 0 ? sortModel[0].sort : "desc", // Default to descending order
      search,
      location: !showOrg && !isAcademyImprovementLead ? userLocationId : "",
    };
    dispatch(fetchDivisions(filters));
  }, [
    page,
    pageSize,
    sort,
    search,
    dispatch,
    userLocationId,
    showOrg,
    isAcademyImprovementLead,
    sortModel,
  ]);

  // When the user stops typing for 500ms, actually apply the search:
  useEffect(() => {
    if (searchInput === search) return; // no need to re-trigger if already current
    const h = setTimeout(() => {
      setPage(1);
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(h);
  }, [searchInput, search, setSearch, setPage]);

  const columns = [
    { field: "name", headerName: "Subject Name", flex: 2, sortable: true },

    (showOrg || isAcademyImprovementLead) && {
      field: "locationCount",
      headerName: "Locations Count",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => {
        const locations = params.row.locations
          .map((location) => location.name)
          .join(", ");
        const locationCount = params.row.locations.length;

        return (
          <Tooltip title={locations} arrow>
            <span>{locationCount}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 1,
      sortable: true,
      renderCell: (params) => moment(params.value).format("ll"),
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      flex: 1,
      sortable: true,
      renderCell: (params) => moment(params.value).format("ll"),
    },

    showOrg && {
      field: "edit",
      headerName: "Edit",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => (
        <button
          onClick={() => handleEditOpen(params.row)}
          style={{ backgroundColor: "transparent", border: "none" }}
        >
          <EditOutlined sx={{ color: primaryDark }} />
        </button>
      ),
    },
    showOrg && {
      field: "delete",
      headerName: "Delete",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => (
        <button
          onClick={() => handleDeleteOpen(params.row)}
          style={{ backgroundColor: "transparent", border: "none" }}
        >
          <DeleteOutlined sx={{ color: "red" }} />
        </button>
      ),
    },
  ].filter(Boolean);

  return {
    backgroundAlt,
    primaryLight,
    page,
    setPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    sort,
    setSort,
    searchInput,
    setSearchInput,
    divisions,
    totalDivisions,
    deleteDivisionDialogOpen,
    reassignDivisionAssessmentsDialogOpen,
    setReassignDivisionAssessmentsDialogOpen,
    closeDeleteDialog,
    handleDeleteDivisionConfirm,
    handleReassignConfirm,
    columns,
    sortModel,
    setSortModel,
  };
};
